import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import * as chatActions from '../../../redux/actions/chatActions';
import ChatMessageList from '../../../components/mobile/Chat/ChatMessageList';
import ChatComposer from '../../../components/mobile/Chat/ChatComposer';
import ChatEmptyState from '../../../components/mobile/Chat/ChatEmptyState';
import Loader from '../../../components/mobile/loader/Loader';
import constants from '../../../constants/constants';
import './Chat.css';

const Chat = () => {
    const dispatch = useDispatch();
    const { messages, isStreaming, isLoadingHistory, activeTool, error, isAvailable } = useSelector((state) => state.chat);

    const [draft, setDraft] = useState('');
    const abortControllerRef = useRef(null);

    useEffect(() => {
        dispatch(chatActions.loadThread());
    }, [dispatch]);

    // Leaving the tab mid-stream aborts the request, which stops the run server-side.
    useEffect(() => {
        return () => abortControllerRef.current?.abort();
    }, []);

    const send = useCallback(
        (text) => {
            const message = text.trim();
            if (!message || isStreaming) return;

            setDraft('');
            abortControllerRef.current = new AbortController();
            dispatch(chatActions.sendMessage(message, abortControllerRef.current));
        },
        [dispatch, isStreaming]
    );

    const handleStop = () => abortControllerRef.current?.abort();

    const handleNewConversation = () => {
        if (isStreaming) return;
        if (messages.length > 0 && !window.confirm(constants.CHAT.NEW_CONVERSATION_CONFIRM)) return;
        dispatch(chatActions.startNewThread());
    };

    if (!isAvailable) {
        return <div className='chat-unavailable'>{constants.CHAT.ERROR_DISABLED}</div>;
    }

    return (
        <div className='chat-page'>
            <header className='chat-header'>
                <div className='chat-header__titles'>
                    <span className='chat-header__title'>{constants.CHAT.TITLE}</span>
                    <span className='chat-header__subtitle'>{constants.CHAT.SUBTITLE}</span>
                </div>
                <button
                    type='button'
                    className='chat-header__new'
                    onClick={handleNewConversation}
                    disabled={isStreaming || messages.length === 0}
                    aria-label={constants.CHAT.NEW_CONVERSATION}
                >
                    <AddCommentOutlinedIcon />
                </button>
            </header>

            {isLoadingHistory ? (
                <div className='chat-page__loader'>
                    <Loader />
                </div>
            ) : messages.length === 0 ? (
                <ChatEmptyState onPickSuggestion={send} disabled={isStreaming} />
            ) : (
                <ChatMessageList messages={messages} isStreaming={isStreaming} activeTool={activeTool} error={error} />
            )}

            <ChatComposer
                value={draft}
                onChange={setDraft}
                onSend={() => send(draft)}
                onStop={handleStop}
                isStreaming={isStreaming}
                disabled={isLoadingHistory}
            />
        </div>
    );
};

export default Chat;
